import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildGapList } from '@/domain/queueEngine';
import type { UnfinishedBusinessRow } from '@/domain/sweeperOutcomes';
import type { Artist, Booking } from '@/domain/types';
import { formatStudioDay } from '@/utils/studioDay';

/**
 * The Sweeper's standing "Unfinished business" tab (B7, 75mt; Anas 1 Oct Q4):
 * no-rebook mid-piece clients from the Floor, whatever gaps their artist has this
 * week, each with the reason, "tattooed N days ago" (no cut-off; the Sweeper
 * decides) and the artist's next open day.
 */
export default function UnfinishedBusinessList({
  rows,
  artists,
  bookings,
  nowIso,
}: {
  rows: UnfinishedBusinessRow[];
  artists: Record<string, Artist>;
  bookings: Record<string, Booking>;
  nowIso: string;
}) {
  const navigate = useNavigate();

  // The artist's next open day (2h+ free), this week first, then the weeks after.
  const nextOpen = useMemo(() => {
    const out = new Map<string, { dayIso: string; openHours: number }>();
    for (const artistId of new Set(rows.map((r) => r.artist?.id).filter((x): x is string => !!x))) {
      const artist = artists[artistId];
      if (!artist) continue;
      const one = { [artistId]: artist };
      const first =
        buildGapList({ artists: one, bookings, nowIso, studioIds: [], weekWindow: 'current' })[0] ??
        buildGapList({ artists: one, bookings, nowIso, studioIds: [], weekWindow: 'next' })
          .sort((a, b) => Date.parse(a.dayIso) - Date.parse(b.dayIso))[0];
      if (first) out.set(artistId, { dayIso: first.dayIso, openHours: first.openHours });
    }
    return out;
  }, [rows, artists, bookings, nowIso]);

  if (rows.length === 0) {
    return (
      <div className="panel-dark p-6 text-center font-serif text-cream-soft max-w-2xl" data-testid="sweeper-unfinished-empty">
        No unfinished business. Every no-rebook mid-piece client has booked again or been closed off.
      </div>
    );
  }

  return (
    <div data-testid="sweeper-unfinished-list">
      <p className="font-serif text-[13px] text-cream-soft mb-4 max-w-3xl leading-relaxed">
        Clients who left the Floor mid-piece with no next session. Call them to book the rest, whatever
        gaps their artist has this week.
      </p>
      <ul>
        {rows.map((row) => {
          const c = row.customer;
          const name = c.preferredName ?? `${c.firstName} ${c.lastName}`;
          const open = row.artist ? nextOpen.get(row.artist.id) : undefined;
          const piece = row.piece?.title ?? row.piece?.style ?? 'Unfinished piece';
          return (
            <li
              key={row.flag.id}
              className="flex items-center gap-4 border-t border-gold-dim/15 py-3.5 first:border-t-0"
              data-testid={`sweeper-unfinished-${c.id}`}
            >
              <div className="flex-1 min-w-0">
                <p className="font-inter text-[13.5px] text-cream font-semibold">{name}</p>
                <p className="font-serif text-[12.5px] text-cream-soft mt-0.5">
                  {piece} with {row.artist?.displayName ?? 'their artist'} · No rebook: {row.flag.reason}
                </p>
                <p className="font-serif text-[12px] text-cream-faint mt-0.5">
                  {open
                    ? `${row.artist?.firstName ?? 'Artist'}'s next open day: ${formatStudioDay(open.dayIso, 'EEE d MMM')} (${open.openHours}h)`
                    : 'No open day in the next few weeks'}
                </p>
                {row.tattooedDays !== undefined ? (
                  <span className="inline-block mt-1.5 rounded-full border border-blood/40 px-2 py-0.5 font-inter text-[10px] uppercase tracking-widest text-blood">
                    Tattooed {row.tattooedDays} {row.tattooedDays === 1 ? 'day' : 'days'} ago
                  </span>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => navigate(`/customer/${c.id}`)}
                className="shrink-0 rounded-md bg-gold-bright px-4 py-2 text-xs font-semibold text-ink-black hover:bg-gold"
                data-testid={`sweeper-unfinished-open-${c.id}`}
              >
                Open record
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
